// EventBus.js - Centralized event system for workflow automation
class EventBus {
  constructor() {
    this.listeners = {}
    this.queue = []
    this.isProcessing = false
    this.eventLog = []
    this.listenerRegistry = new Map() // Track listeners by namespace for selective cleanup
    
    console.log('🚀 EventBus initialized')
  }

  /**
   * Register a listener for a specific event
   * @param {string} eventType - Type of event to listen for
   * @param {function} handler - Callback function when event fires
   * @param {object} options - Additional options (priority, once, namespace)
   * @returns {function} Unsubscribe function
   */
  on(eventType, handler, options = {}) {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = []
    }

    const namespace = options.namespace || 'global'
    const listener = {
      handler,
      priority: options.priority || 0,
      once: options.once || false,
      namespace, // Track which service owns this listener
      id: `${namespace}_${Date.now()}_${Math.random()}` // Include namespace in ID
    }

    this.listeners[eventType].push(listener)
    
    // Sort by priority (higher priority first)
    this.listeners[eventType].sort((a, b) => b.priority - a.priority)

    // Track in registry for namespace-based cleanup
    if (!this.listenerRegistry.has(namespace)) {
      this.listenerRegistry.set(namespace, [])
    }
    this.listenerRegistry.get(namespace).push({
      eventType,
      listenerId: listener.id
    })

    console.log(`📡 Listener registered for event: ${eventType} (namespace: ${namespace})`)

    // Return unsubscribe function
    return () => this.off(eventType, listener.id)
  }

  /**
   * Remove a listener
   * @param {string} eventType - Event type
   * @param {string} listenerId - ID of listener to remove
   */
  off(eventType, listenerId) {
    if (!this.listeners[eventType]) return

    this.listeners[eventType] = this.listeners[eventType].filter(
      listener => listener.id !== listenerId
    )

    console.log(`🔇 Listener removed for event: ${eventType}`)
  }

  /**
   * Emit an event
   * @param {string} eventType - Type of event
   * @param {object} data - Event data
   * @param {object} options - Additional options
   */
  async emit(eventType, data = {}, options = {}) {
    const event = {
      type: eventType,
      data,
      timestamp: new Date().toISOString(),
      id: `event_${Date.now()}_${Math.random()}`,
      source: options.source || 'system'
    }

    console.log(`🔔 Event emitted: ${eventType}`, event)

    // Add to event log
    this.eventLog.push(event)
    if (this.eventLog.length > 100) {
      this.eventLog.shift() // Keep only last 100 events
    }

    // Add to queue if async processing requested
    if (options.async) {
      this.queue.push(event)
      this.processQueue()
      return
    }

    // Process immediately
    await this.processEvent(event)
  }

  /**
   * Process a single event
   * @param {object} event - Event to process
   */
  async processEvent(event) {
    const listeners = this.listeners[event.type] || []

    if (listeners.length === 0) {
      console.log(`⚠️ No listeners for event: ${event.type}`)
      return
    }

    console.log(`⚡ Processing event: ${event.type} with ${listeners.length} listeners`)

    for (const listener of listeners) {
      try {
        await listener.handler(event.data, event)
        
        // Remove if 'once' listener
        if (listener.once) {
          this.off(event.type, listener.id)
        }
      } catch (error) {
        console.error(`❌ Error in listener for ${event.type}:`, error)
      }
    }
  }

  /**
   * Process queued events
   */
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return

    this.isProcessing = true
    console.log(`📋 Processing event queue: ${this.queue.length} events`)

    while (this.queue.length > 0) {
      const event = this.queue.shift()
      await this.processEvent(event)
    }

    this.isProcessing = false
  }

  /**
   * Clear listeners by namespace or all listeners
   * @param {string} namespace - Optional namespace to clear. If not provided, clears ALL listeners.
   */
  clear(namespace = null) {
    if (namespace) {
      this.clearNamespace(namespace)
    } else {
      // Clear everything (use with caution!)
      this.listeners = {}
      this.queue = []
      this.listenerRegistry.clear()
      console.log('🧹 EventBus: Cleared ALL listeners (all namespaces)')
    }
  }

  /**
   * Clear all listeners for a specific namespace
   * @param {string} namespace - Namespace to clear
   */
  clearNamespace(namespace) {
    const entries = this.listenerRegistry.get(namespace) || []
    
    if (entries.length === 0) {
      console.log(`🧹 EventBus: No listeners found for namespace '${namespace}'`)
      return
    }

    // Remove each listener from the listeners map
    entries.forEach(({ eventType, listenerId }) => {
      if (this.listeners[eventType]) {
        this.listeners[eventType] = this.listeners[eventType].filter(
          l => l.id !== listenerId
        )
        
        // Clean up empty event type arrays
        if (this.listeners[eventType].length === 0) {
          delete this.listeners[eventType]
        }
      }
    })

    // Remove from registry
    this.listenerRegistry.delete(namespace)
    
    console.log(`🧹 EventBus: Cleared ${entries.length} listeners for namespace '${namespace}'`)
  }

  /**
   * Get event history
   * @param {number} limit - Number of recent events to return
   * @returns {array} Recent events
   */
  getEventHistory(limit = 20) {
    return this.eventLog.slice(-limit)
  }

  /**
   * Get all registered listeners
   * @returns {object} Listeners by event type
   */
  getListeners() {
    const summary = {}
    Object.keys(this.listeners).forEach(eventType => {
      summary[eventType] = this.listeners[eventType].length
    })
    return summary
  }

  /**
   * Get diagnostic info about registered namespaces
   * @returns {object} Namespace summary with listener counts
   */
  getNamespaces() {
    const summary = {}
    this.listenerRegistry.forEach((entries, namespace) => {
      summary[namespace] = {
        listenerCount: entries.length,
        eventTypes: [...new Set(entries.map(e => e.eventType))]
      }
    })
    return summary
  }
}

// Create singleton instance
const eventBus = new EventBus()

// Event type constants
export const EventTypes = {
  // Lead events
  LEAD_ADDED: 'lead_added',
  LEAD_VALIDATED: 'lead_validated',
  LEAD_ENRICHED: 'lead_enriched',
  LEAD_DELETED: 'lead_deleted',

  // Contact events
  CONTACT_IMPORTED: 'contact_imported',
  CONTACT_UPDATED: 'contact_updated',
  CONTACT_TAGGED: 'contact_tagged',
  CONTACT_UNTAGGED: 'contact_untagged',

  // Email events
  EMAIL_SENT: 'email_sent',
  EMAIL_DELIVERED: 'email_delivered',
  EMAIL_OPENED: 'email_opened',
  EMAIL_CLICKED: 'email_clicked',
  EMAIL_REPLIED: 'email_replied',
  EMAIL_BOUNCED: 'email_bounced',
  EMAIL_UNSUBSCRIBED: 'email_unsubscribed',

  // Campaign events
  CAMPAIGN_CREATED: 'campaign_created',
  CAMPAIGN_STARTED: 'campaign_started',
  CAMPAIGN_PAUSED: 'campaign_paused',
  CAMPAIGN_COMPLETED: 'campaign_completed',
  CAMPAIGN_UPDATED: 'campaign_updated',

  // Deal events
  DEAL_CREATED: 'deal_created',
  DEAL_UPDATED: 'deal_updated',
  DEAL_STAGE_CHANGED: 'deal_stage_changed',
  DEAL_WON: 'deal_won',
  DEAL_LOST: 'deal_lost',
  DEAL_STUCK_IN_STAGE: 'deal_stuck_in_stage',
  DEAL_VALUE_CHANGED: 'deal_value_changed',
  DEAL_PROBABILITY_CHANGED: 'deal_probability_changed',

  // Appointment events
  APPOINTMENT_BOOKED: 'appointment_booked',
  APPOINTMENT_CONFIRMED: 'appointment_confirmed',
  APPOINTMENT_CANCELLED: 'appointment_cancelled',
  APPOINTMENT_COMPLETED: 'appointment_completed',

  // Form events
  FORM_SUBMITTED: 'form_submitted',
  FORM_ABANDONED: 'form_abandoned',

  // Workflow events
  WORKFLOW_STARTED: 'workflow_started',
  WORKFLOW_COMPLETED: 'workflow_completed',
  WORKFLOW_FAILED: 'workflow_failed',

  // System events
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  API_KEY_ADDED: 'api_key_added',
  INTEGRATION_CONNECTED: 'integration_connected'
}

export default eventBus
